/**
 * PaymentReturn — Page shown after Stripe redirects the user back.
 *
 * Success URL:   /payment/return?session_id=cs_xxx&subdomain=xxx
 * Cancelled URL: /payment/return?status=cancelled&subdomain=xxx
 *
 * Polls Stripe via the SaaS API and shows success / failure UI.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { registrationApi } from '../services/saasApi';
import MaweredLogo from '../components/MaweredLogo';

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2500;

const PaymentReturn = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const subdomain = searchParams.get('subdomain');
  const urlStatus = searchParams.get('status') || '';
  const sessionId = searchParams.get('session_id');
  const ref = sessionId;

  const [state, setState]       = useState('loading'); // loading | success | failed | error
  const [pollCount, setPollCount] = useState(0);
  const [loginUrl, setLoginUrl]  = useState(null);

  useEffect(() => {
    // Immediate failure from gateway redirect URL
    if (urlStatus === 'failed' || urlStatus === 'cancelled') {
      setState('failed');
      return;
    }

    // No ref to verify — check URL status
    if (!ref) {
      if (urlStatus === 'completed' || urlStatus === 'paid' || urlStatus === 'success') {
        setState('success');
        if (subdomain) setLoginUrl(`https://${subdomain}.mawered.com/login`);
      } else {
        setState('error');
      }
      return;
    }

    let cancelled = false;
    let polls = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const result = await registrationApi.getStripeSessionStatus(ref);
        // Stripe session: payment_status "paid" | "unpaid"; status "complete" | "open" | "expired"
        const paid     = result.payment_status === 'paid';
        const complete = result.status === 'complete';
        const expired  = result.status === 'expired';

        if (paid && complete) {
          if (!cancelled) {
            setState('success');
            if (subdomain) setLoginUrl(`https://${subdomain}.mawered.com/login`);
          }
          return;
        }
        if (expired || (result.payment_status === 'unpaid' && result.status !== 'open')) {
          if (!cancelled) setState('failed');
          return;
        }

        // Still pending — keep polling
        polls++;
        setPollCount(polls);
        if (polls < MAX_POLLS) {
          setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          if (!cancelled) setState('error');
        }
      } catch {
        if (!cancelled) setState('error');
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [ref, urlStatus, subdomain]);

  const gatewayName = 'Stripe';

  return (
    <div className="dark min-h-screen flex flex-col items-center justify-center p-6" style={{ backgroundColor: "var(--lm-canvas)" }}>
      <div className="mb-8">
        <MaweredLogo size="lg" />
      </div>

      <div className="p-10 max-w-md w-full text-center rounded-xl" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>

        {state === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-indigo-500 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {t('paymentReturn.verifying')}
            </h2>
            <p className="text-muted-foreground">
              {t('paymentReturn.verifyingWith', { gateway: gatewayName })}
            </p>
            {pollCount > 0 && (
              <p className="text-xs text-gray-400 mt-4">
                {t('paymentReturn.attempt', { current: pollCount, max: MAX_POLLS })}
              </p>
            )}
          </>
        )}

        {state === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {t('paymentReturn.successTitle')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('paymentReturn.successMessage')}
              {subdomain && (
                <> {t('paymentReturn.yourSpace')} <strong>{subdomain}.mawered.com</strong>.</>
              )}
            </p>
            {loginUrl ? (
              <a
                href={loginUrl}
                className="inline-flex items-center gap-2 px-8 py-3 text-white font-semibold rounded-xl transition-all"
                style={{ backgroundColor: "#5e6ad2" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#828fff")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
              >
                {t('paymentReturn.accessMySpace')}
                <ArrowRight className="w-5 h-5" />
              </a>
            ) : (
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all"
              >
                <Home className="w-5 h-5" />
                {t('paymentReturn.backHome')}
              </Link>
            )}
          </>
        )}

        {state === 'failed' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {t('paymentReturn.failedTitle')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('paymentReturn.failedMessage')}
            </p>
            <div className="flex flex-col gap-3">
              <Link
                to="/register"
                className="px-8 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all"
              >
                {t('paymentReturn.retryRegistration')}
              </Link>
              <Link
                to="/"
                className="px-8 py-3 border border-border text-foreground font-medium rounded-xl hover:bg-accent transition-all"
              >
                {t('paymentReturn.backHome')}
              </Link>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-amber-950/30 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⏳</span>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {t('paymentReturn.pendingTitle')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('paymentReturn.pendingMessage')}
            </p>
            {subdomain && (
              <a
                href={`https://${subdomain}.mawered.com/login`}
                className="px-8 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all"
              >
                {t('paymentReturn.tryLogin')}
              </a>
            )}
          </>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-6">
        {t('paymentReturn.support')}{' '}
        <a href="mailto:support@mawered.com" className="underline hover:text-muted-foreground">
          support@mawered.com
        </a>
      </p>
    </div>
  );
};

export default PaymentReturn;
